import {
  ApplicationConfig,
  inject,
  provideAppInitializer,
  provideBrowserGlobalErrorListeners,
  provideZoneChangeDetection,
} from '@angular/core';
import { provideRouter } from '@angular/router';
import { provideHttpClient, withInterceptors, withXhr } from '@angular/common/http';
import { provideTranslateService, TranslateService } from '@ngx-translate/core';
import { provideTranslateHttpLoader } from '@ngx-translate/http-loader';
import { firstValueFrom, timeout } from 'rxjs';
import { appRoutes } from './app.routes';
import { AuthService, AUTH_SHOW_SIGNUP, PermissionService } from '@zag/auth/ui';
import {
  BULK_JOBS_GATEWAY,
  FEATURE_CHECKER,
  FIELD_PERMISSION_CHECKER,
  LanguageService,
  ThemeService,
  languageHeaderInterceptor,
  USER_ID_PROVIDER,
} from '@zag/ui';
import { FeatureAccessClientService } from '@zag/features/ui';
import { JobsGatewayClient } from '@zag/jobs/ui';
import { provideControlBreadcrumbConfig } from './breadcrumb.providers';

export const appConfig: ApplicationConfig = {
  providers: [
    provideBrowserGlobalErrorListeners(),
    provideZoneChangeDetection({ eventCoalescing: true }),
    provideRouter(appRoutes),
    { provide: BULK_JOBS_GATEWAY, useExisting: JobsGatewayClient },
    { provide: FIELD_PERMISSION_CHECKER, useExisting: PermissionService },
    { provide: FEATURE_CHECKER, useExisting: FeatureAccessClientService },
    {
      provide: USER_ID_PROVIDER,
      useFactory: () => {
        const auth = inject(AuthService);
        return () => auth.currentUser()?.id;
      },
    },
    provideHttpClient(withXhr(), withInterceptors([languageHeaderInterceptor])),
    { provide: AUTH_SHOW_SIGNUP, useValue: false },
    ...provideControlBreadcrumbConfig(),
    // Why: fallbackLang triggers a constructor-time request before subscribers are wired.
    provideTranslateService({}),
    provideTranslateHttpLoader({
      prefix: '/api/v1/i18n/',
      suffix: '',
    }),
    // Load translations with browser language detection
    provideAppInitializer(async () => {
      const translate = inject(TranslateService);
      const languageService = inject(LanguageService);

      try {
        const language = languageService.initializeLanguage();
        translate.setFallbackLang('en');
        await firstValueFrom(translate.use(language).pipe(timeout(5000)));
      } catch (error) {
        console.warn('Failed to load translations, using keys as fallback:', error);
      }
    }),
    // Initialize theme
    provideAppInitializer(() => {
      const themeService = inject(ThemeService);
      const theme = themeService.initializeTheme();
      themeService.applyThemeToDocument(theme);

      if (typeof document !== 'undefined') {
        document.documentElement.classList.add('theme-transition');
      }
    }),
    // Check for existing session on app startup
    provideAppInitializer(async () => {
      const authService = inject(AuthService);

      try {
        await authService.getSession();
      } catch {
        // Session check failed - user not logged in, this is fine
        console.debug('No active session found');
      }
    }),
  ],
};
